import {
  Link,
  useParams,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom';
import { Button } from '@nike/eds';
import { ArrowLeft } from '@nike/nike-design-system-icons';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';

import Loader from 'components/Loader';

import { QUERY_KEYS } from '../../constants';
import labellinClientAPI from '../../clientAPI/labelling';
import propagate403Error from '../../utils/propagate403Error';

export default function LabellingReport() {
  const { datasetId } = useParams();
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();

  const { status, data, error } = useQuery({
    queryKey: [QUERY_KEYS.LABELLING_DATASET_REPORT, datasetId],
    queryFn: async () =>
      labellinClientAPI.findDatasetReportById(
        datasetId!,
        loaderData.access_token,
      ),
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  propagate403Error(error as keyable);
  if (status === 'loading') return <Loader />;
  if (!data) {
    return (
      <div className='tw-w-full tw-h-2/3 tw-flex tw-items-center tw-justify-center'>
        <h2 className='eds-type--display-4'>No report available</h2>
      </div>
    );
  }
  return (
    <div className='tw-mx-auto tw-py-10 tw-w-5/12'>
      <div className='tw-flex tw-justify-between'>
        <Button
          size='small'
          variant='ghost'
          beforeSlot={<ArrowLeft />}
          as={Link}
          to='/labelling/datasets'
        >
          Dataset List
        </Button>
      </div>

      <p className='eds-type--display-5 tw-mb-4 tw-mt-10'>Labelling State</p>
      <div className='tw-flex tw-mt-7'>
        <div className='tw-mr-24'>
          <p className='tw-text-6xl tw-font-thin'>{`${(
            data.coverage * 100
          ).toFixed(0)}%`}</p>
          <p className='tw-text-sm tw-mt-1'>Labelling Coverage</p>
        </div>

        <div className='tw-mr-24'>
          <p className='tw-text-6xl tw-font-thin'>{data.labelersCount}</p>
          <p className='tw-text-sm tw-mt-1'>Labelling People</p>
        </div>

        <div>
          <p className='tw-text-6xl tw-font-thin'>{data.threadsSize}</p>
          <p className='tw-text-sm tw-mt-1 tw-pl-1'>Data Size</p>
        </div>
      </div>

      <p className='eds-type--display-5 tw-mt-20 tw-mb-4'>Labelling Progress</p>
      <div
        className='tw-flex-1 tw-flex tw-flex-wrap tw-gap-2'
        style={{ width: '44rem' }}
      >
        {data.productStats.map((productState) => {
          return (
            <Link
              key={productState.threadId}
              to={`/labelling/datasets/${datasetId}/products/${productState.threadId}`}
              className={classnames(
                'tw-w-5 tw-h-5 tw-rounde tw-transition-all hover:tw-scale-125',
                {
                  'tw-bg-green-900':
                    productState.labelledAmount === data.labelersCount,
                  'tw-bg-green-500':
                    productState.labelledAmount > 0 &&
                    productState.labelledAmount < data.labelersCount,
                  'tw-bg-gray-300': productState.labelledAmount === 0,
                },
              )}
            />
          );
        })}
      </div>
      <div className='tw-flex tw-items-center tw-mt-3'>
        <div className='tw-flex tw-items-center tw-mr-5'>
          <div className='tw-w-3 tw-h-3 tw-rounded tw-bg-green-900 tw-mr-1' />
          <p className='tw-text-sm'>Everybody labelled</p>
        </div>
        <div className='tw-flex tw-items-center tw-mr-5'>
          <div className='tw-w-3 tw-h-3 tw-rounded tw-bg-green-500 tw-mr-1' />
          <p className='tw-text-sm'>Somebody labelled</p>
        </div>
        <div className='tw-flex tw-items-center'>
          <div className='tw-w-3 tw-h-3 tw-rounded tw-bg-gray-300 tw-mr-1' />
          <p className='tw-text-sm'>Nobody labelled</p>
        </div>
      </div>
    </div>
  );
}
